export * from "./generated/api";
export * from "./generated/api.schemas";
export { setBaseUrl, setAuthTokenGetter, ApiError } from "./custom-fetch";
export type { AuthTokenGetter } from "./custom-fetch";

// Explicit named re-exports to guarantee Rollup can resolve these symbols in
// production builds even when its export* static analysis is unreliable.
export {
  getListJobsQueryKey,
  getGetJobQueryKey,
  getGetJobStatsQueryKey,
  getGetJobTimingStatsQueryKey,
  useListJobs,
  useGetJob,
  useGetJobStats,
  useGetJobTimingStats,
  useDeleteJob,
  useRegenerateJob,
  useRetryJob,
  getRssEpisodes,
  createRssJob,
  createSubscription,
  listSocialAccounts,
  listSocialPosts,
  updateSocialPost,
  approveSocialPost,
  discardSocialPost,
  retrySocialPost,
  reopenSocialPost,
  disconnectSocialAccount,
  useGetCurrentUser,
  useUpdateUserPreferences,
  getCurrentUser,
  updateUserPreferences,
  renderQuoteVideo,
  renderQuoteVideo as renderQuoteVideoApi,
  getQuoteVideoStatus,
  getDownloadQuoteVideoUrl,
  getQuoteVideoUrl as getQuoteVideoDownloadLink,
  listQuoteVideos,
  updateQuoteVideoPreference,
  useRenderQuoteVideo,
  useGetQuoteVideoStatus,
  useListQuoteVideos,
  useUpdateQuoteVideoPreference,
} from "./generated/api";
export type {
  RssEpisode,
  RssSubscription,
  SocialPost,
  SocialAccount,
  QuoteVideo,
  UpdateSubscriptionBody,
} from "./generated/api.schemas";

import { customFetch } from "./custom-fetch";
import type { BackfillSubscriptionBody } from "./generated/api.schemas";

export async function backfillSubscriptionByUrls(
  id: number,
  episodeUrls: string[],
): Promise<{ queued: boolean; remainingSeconds?: number; warning?: string }> {
  const body: BackfillSubscriptionBody = {
    episodeUrls,
  } as BackfillSubscriptionBody;
  return customFetch<{
    queued: boolean;
    remainingSeconds?: number;
    warning?: string;
  }>(`/api/rss/subscriptions/${id}/backfill`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function testSocialAccount(
  id: number,
): Promise<{ success: boolean; requiresReconnect: boolean; message: string }> {
  return customFetch<{
    success: boolean;
    requiresReconnect: boolean;
    message: string;
  }>(`/api/social/accounts/${id}/test`, {
    method: "POST",
  });
}

/**
 * Returns the URL to begin an OAuth connection flow for the given platform.
 *
 * **Usage:** Navigate the browser to this URL — do NOT fetch it via
 * React Query or XMLHttpRequest. The server responds with a 302 redirect
 * to the platform's OAuth consent screen; fetching it will fail due to CORS.
 *
 * @example
 *   window.location.href = getSocialOAuthUrl('youtube');
 */
export function getSocialOAuthUrl(platform: "youtube" | "linkedin"): string {
  const basePath: string =
    (typeof import.meta !== "undefined" &&
      (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL) ||
    "/";
  const base = (window.location.origin + basePath).replace(/\/$/, "");
  return `${base}/api/social/oauth/${platform}/start`;
}
